import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { forkJoin, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { Domain, LearningObjectiveOrder } from "../model/model";
import { DomainService } from "../services/domain.service";
import { GroupService } from "../services/group.service";
import { LearningObjectiveOrderService } from "../services/learning-objective-order.service";
import { SchoolService } from "../services/school.service";
import { UserService } from "../services/user.service";
import {
  SetSelectedLearningObjectiveOrderAction,
  UpdateSelectedLearningObjectiveOrderTitleAction,
} from "./learning-objectives.state";

export class LoadLearningObjectiveOrderAction {
  static type = "[LearningObjective Order] Load ";
  constructor() {}
}

export class LoadLearningObjectiveOrderListsAction {
  static type = "[LearningObjective Order] Load learning objective order lists";
  constructor() {}
}
export class SelectDomainAction {
  static type = "[LearningObjective Order] Select domain ";
  constructor(public domainId: string) {}
}

export class DisableLearningObjectiveInStructureAction {
  static type =
    "[LearningObjective Order] disable learning objective in structure";
  constructor(public learningObjectiveId: string) {}
}

export class EnableLearningObjectiveInStructureAction {
  static type =
    "[LearningObjective Order] enable learning objective in structure";
  constructor(public learningObjectiveId: string) {}
}

export class SelectOrCopyLearningObjectiveOrderAction {
  static type =
    "[LearningObjective Order] Select (if owner) or copy (if template or school)";
  constructor(public learningObjectiveOrderId: string) {}
}

export class EnterEditModeAction {
  static type =
    "[LearningObjective Order] Enter learning objective order edit-mode";
}

export class EnterReadOnlyModeAction {
  static type =
    "[LearningObjective Order] Enter learning objective order readonly-mode";
}

export class AddLearningObjectiveOrderAction {
  static type = "[LearningObjective Order] Add learning objective order";
  constructor(public basedOn: LearningObjectiveOrder) {}
}

export class DeleteLearningObjectiveOrderAction {
  static type = "[LearningObjective Order] Delete learning objective order";
  constructor(public id: string) {}
}

export class UpdateLearningObjectiveOrderTitleAction {
  static type =
    "[LearningObjective Order] Update title select learning Objective order";
  constructor(public id: string, public title: string) {}
}

export const initialState = {
  listSelected: true,
  editMode: false,
  isLoading: true,
  domains: null,
  selectedDomainId: "",
  myLists: [],
  templateLists: null,
  groupname: "",
  isCoordinator: false,
  learningObjectiveInStructure: "",
};

export interface ListData {
  myLists: Array<LearningObjectiveOrder>;
  templateLists: Array<LearningObjectiveOrder>;
}

export interface LearningObjectiveOrderStateModel {
  editMode: boolean;
  isLoading: boolean;
  domains: Array<Domain>;
  selectedDomainId: string;
  myLists: Array<LearningObjectiveOrder>;
  templateLists: Array<LearningObjectiveOrder>;
  groupname: string;
  isCoordinator: boolean;
  learningObjectiveInStructure: string;
}
@State<LearningObjectiveOrderStateModel>({
  name: "learningObjectiveOrder",
  defaults: initialState,
})
@Injectable()
export class LearningObjectiveOrderState {
  constructor(
    private domainService: DomainService,
    private store: Store,
    private userService: UserService,
    private schoolService: SchoolService,
    private groupService: GroupService,
    private learningObjectiveOrderService: LearningObjectiveOrderService
  ) {}

  @Selector()
  static domains(state: LearningObjectiveOrderStateModel) {
    return state.domains;
  }

  @Selector()
  static selectedDomain(state: LearningObjectiveOrderStateModel) {
    if (state.domains) {
      const domain = state.domains.find((d) => d.id === state.selectedDomainId);
      if (domain) {
        return domain;
      }
      // TODO: somehow state.selectedDomainId is defaulted to initial state.
      // because there's always a domain selected we can just grap the first one.
      // but we should find out what defaults it.
      return state.domains.length > 0 ? state.domains[0] : null;
    }
    return null;
  }

  @Selector()
  static myLists(state: LearningObjectiveOrderStateModel): ListData {
    return { myLists: state.myLists, templateLists: state.templateLists };
  }

  @Selector()
  static groupname(state: LearningObjectiveOrderStateModel) {
    return state.groupname;
  }

  @Selector()
  static isCoordinator(state: LearningObjectiveOrderStateModel) {
    return state.isCoordinator;
  }

  @Selector()
  static templateLists(state: LearningObjectiveOrderStateModel) {
    return state.templateLists;
  }

  @Action(EnterEditModeAction)
  enterEditModeAction(ctx: StateContext<LearningObjectiveOrderStateModel>) {
    ctx.patchState({ editMode: true });
    //return ctx.dispatch(new ClearFiltersAction());
  }

  @Action(EnterReadOnlyModeAction)
  enterReadOnlyModeAction(ctx: StateContext<LearningObjectiveOrderStateModel>) {
    ctx.patchState({ editMode: false });
  }

  @Action(UpdateLearningObjectiveOrderTitleAction)
  updateSelectedLearningObjectiveOrderTitleAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: UpdateLearningObjectiveOrderTitleAction
  ) {
    const currentState = ctx.getState();
    const newList = currentState.myLists.map((l) =>
      l.id === action.id ? { ...l, title: action.title } : l
    );
    ctx.patchState({ myLists: newList });
    return ctx.dispatch(
      new UpdateSelectedLearningObjectiveOrderTitleAction(
        action.id,
        action.title
      )
    );
  }

  @Action(LoadLearningObjectiveOrderListsAction)
  loadLearningObjectiveOrderListsAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>
  ) {
    console.log("load lists");
    const state = ctx.getState();
    return this.schoolService.getLearningObjectiveOrder().pipe(
      map((lists) => {
        const groups = this.userService.user.groupList;
        const myList = lists.filter(
          (l) =>
            !l.isTemplate &&
            groups.some(function (item) {
              return item === l.groupId;
            })
        );
        const templateLists = lists.filter(
          (l) => l.isTemplate && l.learningObjectives.length > 0
        );
        ctx.dispatch(
          new SetSelectedLearningObjectiveOrderAction(
            templateLists[0] ? templateLists[0].id : myList[0].id
          )
        );
        ctx.patchState({
          myLists: myList,
          templateLists: templateLists,
          isLoading: false,
        });
      })
    );
  }

  @Action(LoadLearningObjectiveOrderAction)
  loading(ctx: StateContext<LearningObjectiveOrderStateModel>) {
    console.log("load Learning obj order");
    const state = ctx.getState();
    // TODO dont retrieve when it's already there.
    return forkJoin(
      this.domainService.get(),
      this.schoolService.getLearningObjectiveOrder(),
      state.groupname
        ? of(state.groupname)
        : this.groupService
            .get(this.userService.user.groupList[0])
            .pipe(map((g) => (g ? g.name : ""))),
      state.isCoordinator
        ? of(state.isCoordinator)
        : this.userService.isCoordinator()
    ).pipe(
      map(([ds, lists, groupname, isCoordinator]) => {
        lists = lists && lists.length > 0 ? lists : [];
        const groups = this.userService.user.groupList;
        const myList = lists.filter(
          (l) =>
            !l.isTemplate &&
            groups.some(function (item) {
              return item === l.groupId;
            })
        );
        const templateLists = lists.filter(
          (l) => l.isTemplate && l.learningObjectives.length > 0
        );
        ctx.patchState({
          // don't use the const state here because this can be an outdated value
          // we need the current value here.
          selectedDomainId: ds.length > 0 ? ds[0].id : "",
          editMode: myList.length === 0 ? false : ctx.getState().editMode,
          groupname: groupname,
          isCoordinator: isCoordinator,
          domains: ds,
          myLists: myList,
          templateLists: templateLists,
          isLoading: false,
        });
      })
    );
  }

  @Action(AddLearningObjectiveOrderAction)
  AddLearningObjectiveOrderAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: AddLearningObjectiveOrderAction
  ) {
    const state = ctx.getState();
    const title = action.basedOn
      ? `${action.basedOn.title} - ${this.getDate()}`
      : `Eigen lijst - ${this.getDate()}`;
    ctx.patchState({ isLoading: true });
    const learningObjectives = action.basedOn
      ? action.basedOn.learningObjectives
      : null;
    return this.learningObjectiveOrderService
      .add(title, learningObjectives)
      .pipe(
        tap((l) => {
          ctx.patchState({ myLists: [...state.myLists, l] });
          ctx.dispatch(new SetSelectedLearningObjectiveOrderAction(l.id));
          ctx.patchState({ isLoading: false });
          return ctx.dispatch(new LoadLearningObjectiveOrderAction());
        })
      );
  }

  @Action(DeleteLearningObjectiveOrderAction)
  DeleteLearningObjectiveOrderAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: DeleteLearningObjectiveOrderAction
  ) {
    const state = ctx.getState();
    ctx.patchState({ isLoading: true });
    return this.learningObjectiveOrderService.delete(action.id).pipe(
      map((result) => {
        if (result) {
          const list = state.myLists.length >= 0 ? state.myLists[0] : null;
          ctx.patchState({
            myLists: state.myLists.filter((l) => l.id !== action.id),
            isLoading: false,
          });
          ctx.dispatch(
            new SetSelectedLearningObjectiveOrderAction(list ? list.id : "")
          );
          return ctx.dispatch(new LoadLearningObjectiveOrderAction());
        }
      })
    );
  }

  @Action(SelectOrCopyLearningObjectiveOrderAction)
  SelectOrCopyLearningObjectiveOrderAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: SelectOrCopyLearningObjectiveOrderAction
  ) {
    const state = ctx.getState();
    const myTemplate = state.templateLists.find(
      (l) => l.id === action.learningObjectiveOrderId
    );
    if (myTemplate) {
      return ctx.dispatch(new AddLearningObjectiveOrderAction(myTemplate));
    } else {
      return ctx.dispatch(
        new SetSelectedLearningObjectiveOrderAction(
          action.learningObjectiveOrderId
        )
      );
    }
  }

  @Action(SelectDomainAction)
  selectingDomain(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: SelectDomainAction
  ) {
    ctx.patchState({ selectedDomainId: action.domainId });
  }

  @Action(EnableLearningObjectiveInStructureAction)
  enableLearningObjectiveInStructureAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: EnableLearningObjectiveInStructureAction
  ) {
    const state = ctx.getState();
    ctx.patchState({
      domains: state.domains.map((d) =>
        d.id === state.selectedDomainId
          ? this.setEnabledLearningObjective(d, action.learningObjectiveId)
          : d
      ),
    });
  }

  @Action(DisableLearningObjectiveInStructureAction)
  disableLearningObjectiveInStructureAction(
    ctx: StateContext<LearningObjectiveOrderStateModel>,
    action: DisableLearningObjectiveInStructureAction
  ) {
    const state = ctx.getState();
    ctx.patchState({
      domains: state.domains.map((d) =>
        d.id === state.selectedDomainId
          ? this.setDisabledLearningObjective(d, action.learningObjectiveId)
          : d
      ),
    });
  }

  private getDate = (): string =>
    `${new Date().getDate()}-${
      new Date().getMonth() + 1
    }-${new Date().getFullYear()}`;

  private setEnabledLearningObjective = (domain: Domain, selected: string) => {
    domain.subjects = domain.subjects.map((s) => {
      s.goals = s.goals.map((g) => {
        g.learningObjectives = g.learningObjectives.map((l) => {
          const retL = selected === l.id ? { ...l, inList: false } : l;
          retL.learningObjectives = l.learningObjectives.map((subl) =>
            selected === subl.id ? { ...subl, inList: false } : subl
          );
          return retL;
        });
        return g;
      });
      return s;
    });
    return domain;
  };

  private setDisabledLearningObjective = (
    domain: Domain,
    unselected: string
  ) => {
    domain.subjects = domain.subjects.map((s) => {
      s.goals = s.goals.map((g) => {
        g.learningObjectives = g.learningObjectives.map((l) => {
          const retL = unselected === l.id ? { ...l, inList: true } : l;
          retL.learningObjectives = l.learningObjectives.map((subl) =>
            unselected === subl.id ? { ...subl, inList: true } : subl
          );
          return retL;
        });
        return g;
      });
      return s;
    });
    return domain;
  };
}
